import React, { useState, useEffect } from 'react';
import { 
  FileBarChart, 
  Download, 
  Eye,
  FileText,
  ChevronDown,
  BarChart3,
  Shield
} from 'lucide-react';
import { reportAPI, ReportTemplate, ReportRequest } from '../services/reportAPI';

interface ReportTemplateUI extends ReportTemplate {
  icon: React.ComponentType<any>;
}

interface ReportsManagerProps {
  enquete?: any; // Données de l'enquête sélectionnée
  enquetes?: any[]; // Liste de toutes les enquêtes disponibles
}

const ReportsManager: React.FC<ReportsManagerProps> = ({ enquete, enquetes = [] }) => {
  const [selectedTemplate, setSelectedTemplate] = useState<string>('standard');
  const [selectedEnqueteId, setSelectedEnqueteId] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [activeTab, setActiveTab] = useState<'generate' | 'history' | 'analytics'>('generate');
  const [reportOptions] = useState({
    delivery_method: 'download' as 'download' | 'email' | 'both',
    confidentiality_level: 'confidential' as 'public' | 'internal' | 'confidential' | 'secret',
    expires_in_days: 30
  });

  // Utiliser l'enquête sélectionnée via la liste déroulante ou celle passée en prop
  const currentEnquete = selectedEnqueteId 
    ? enquetes.find(e => e.id === selectedEnqueteId) 
    : enquete;

  // Charger les templates
  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setIsLoadingTemplates(true);
    try {
      const templateData = await reportAPI.getTemplates();
      setTemplates(templateData || []);
    } catch (error) {
      console.error('Erreur lors du chargement des templates:', error);
      // Set fallback templates
      setTemplates([
        {
          id: 'standard',
          name: 'Rapport Standard',
          description: 'Rapport d\'enquête standard avec toutes les sections de base',
          type: 'standard',
          sections: [
            { id: '1', title: 'Résumé exécutif', type: 'text', content: '', order: 1, visible: true, mandatory: true },
            { id: '2', title: 'Contexte de l\'enquête', type: 'text', content: '', order: 2, visible: true, mandatory: true },
            { id: '3', title: 'Méthodologie', type: 'text', content: '', order: 3, visible: true, mandatory: false },
            { id: '4', title: 'Résultats OSINT', type: 'table', content: '', order: 4, visible: true, mandatory: true },
            { id: '5', title: 'Personnes impliquées', type: 'table', content: '', order: 5, visible: true, mandatory: false },
            { id: '6', title: 'Lieux d\'intérêt', type: 'map', content: '', order: 6, visible: true, mandatory: false },
            { id: '7', title: 'Chronologie', type: 'timeline', content: '', order: 7, visible: true, mandatory: false },
            { id: '8', title: 'Conclusions', type: 'text', content: '', order: 8, visible: true, mandatory: true },
            { id: '9', title: 'Recommandations', type: 'text', content: '', order: 9, visible: true, mandatory: false },
            { id: '10', title: 'Annexes', type: 'text', content: '', order: 10, visible: true, mandatory: false }
          ],
          layout: {
            format: 'A4',
            orientation: 'portrait',
            margins: { top: 20, right: 20, bottom: 20, left: 20 },
            header: { enabled: true, height: 50, content: 'RAPPORT D\'ENQUÊTE OSINT' },
            footer: { enabled: true, height: 30, content: 'Page {page} sur {total}' }
          },
          branding: {
            company_name: 'NetMeSafe',
            colors: { primary: '#FF5722', secondary: '#2D3748', accent: '#4299E1' },
            fonts: { primary: 'Arial', secondary: 'Helvetica' }
          },
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          created_by: 'system'
        }
      ]);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  const getIconForTemplate = (type: string) => {
    switch (type) {
      case 'standard': return FileBarChart;
      case 'executive': return Eye;
      case 'technical': return FileText;
      case 'legal': return Shield;
      case 'insurance': return BarChart3;
      default: return FileText;
    }
  };

  const reportTemplatesUI: ReportTemplateUI[] = (templates || []).map(template => ({
    ...template,
    icon: getIconForTemplate(template.type)
  }));

  const handleGenerateReport = async () => {
    if (!currentEnquete) {
      alert('Veuillez sélectionner une enquête pour générer le rapport');
      return;
    }

    setIsGenerating(true);
    try {
      const reportRequest: ReportRequest = {
        enquete_id: currentEnquete.id,
        template_id: selectedTemplate,
        title: `Rapport d'enquête - ${currentEnquete.titre}`,
        subtitle: `Client: ${typeof currentEnquete.client === 'object' ? currentEnquete.client.nom : currentEnquete.client}`,
        delivery_method: reportOptions.delivery_method,
        confidentiality_level: reportOptions.confidentiality_level,
        expires_in_days: reportOptions.expires_in_days
      };

      const generatedReport = await reportAPI.generateReport(reportRequest);
      
      if (generatedReport.download_url) {
        // Télécharger automatiquement le rapport
        window.open(generatedReport.download_url, '_blank');
      } else {
        // Obtenir l'URL de téléchargement
        const downloadData = await reportAPI.downloadReport(generatedReport.id);
        window.open(downloadData.download_url, '_blank');
      }

      // Recharger les templates
      await loadTemplates();
      
      alert('Rapport généré avec succès !');
    } catch (error) {
      console.error('Erreur lors de la génération du rapport:', error);
      alert('Erreur lors de la génération du rapport: ' + (error as Error).message);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Gestion des Rapports</h2>
          <p className="text-gray-400">Générer des rapports professionnels ultra-propres</p>
        </div>
        <div className="flex items-center space-x-3">
          {/* Tabs */}
          <div className="flex bg-osint-light-gray rounded-lg p-1">
            <button
              onClick={() => setActiveTab('generate')}
              className={`px-4 py-2 rounded-md text-sm transition-colors ${
                activeTab === 'generate' 
                  ? 'bg-osint-orange text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Générer
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-4 py-2 rounded-md text-sm transition-colors ${
                activeTab === 'history' 
                  ? 'bg-osint-orange text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Historique
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`px-4 py-2 rounded-md text-sm transition-colors ${
                activeTab === 'analytics' 
                  ? 'bg-osint-orange text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Analytics
            </button>
          </div>
          
          {activeTab === 'generate' && (
            <button
              onClick={handleGenerateReport}
              disabled={isGenerating || !currentEnquete || isLoadingTemplates}
              className="flex items-center space-x-2 px-4 py-2 bg-osint-orange hover:bg-orange-600 disabled:bg-gray-600 rounded-lg text-white text-sm transition-colors"
            >
              {isGenerating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Génération...</span>
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  <span>Générer le rapport</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Contenu selon l'onglet */}
      {activeTab === 'generate' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sélection de l'enquête et du modèle */}
        <div className="lg:col-span-1">
          <div className="osint-card p-6 rounded-lg space-y-6">
            {/* Sélection de l'enquête */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Sélection de l'enquête</h3>
              <div className="relative">
                <select
                  value={selectedEnqueteId}
                  onChange={(e) => setSelectedEnqueteId(e.target.value)}
                  className="w-full p-3 bg-osint-light-gray border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-osint-orange focus:border-transparent appearance-none"
                >
                  <option value="">Sélectionner une enquête</option>
                  {enquetes.map((enquete) => (
                    <option key={enquete.id} value={enquete.id}>
                      {enquete.titre} - {typeof enquete.client === 'object' ? enquete.client.nom : enquete.client}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Modèles de rapports */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Modèles de rapports</h3>
              <div className="space-y-3">
                {isLoadingTemplates ? (
                  <div className="text-center py-4">
                    <div className="w-8 h-8 border-2 border-osint-orange border-t-transparent rounded-full animate-spin mx-auto"></div>
                    <p className="text-gray-400 mt-2">Chargement des templates...</p>
                  </div>
                ) : reportTemplatesUI.length > 0 ? (
                  reportTemplatesUI.map((template: ReportTemplateUI) => {
                    const Icon = template.icon;
                    return (
                      <button
                        key={template.id}
                        onClick={() => setSelectedTemplate(template.id)}
                        className={`w-full flex items-center space-x-3 p-3 rounded-lg text-left transition-colors ${
                          selectedTemplate === template.id
                            ? 'bg-osint-orange text-white'
                            : 'text-gray-300 hover:bg-osint-light-gray'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        <div>
                          <p className="font-medium">{template.name}</p>
                          <p className="text-sm opacity-75">{template.description}</p>
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <div className="text-center py-4">
                    <p className="text-gray-400">Aucun template disponible</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Configuration et aperçu */}
        <div className="lg:col-span-2 space-y-6">
          {/* Informations de l'enquête */}
          <div className="osint-card p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-white mb-4">Informations de l'enquête</h3>
            {currentEnquete ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Titre</span>
                  <span className="text-white font-medium">{currentEnquete.titre}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Client</span>
                  <span className="text-white font-medium">{typeof currentEnquete.client === 'object' ? currentEnquete.client.nom : currentEnquete.client}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Statut</span>
                  <span className="text-white font-medium">{currentEnquete.statut}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Priorité</span>
                  <span className="text-white font-medium">{currentEnquete.priorite}</span>
                </div>
              </div>
            ) : (
              <p className="text-gray-400">Aucune enquête sélectionnée</p>
            )}
          </div>

          {/* Aperçu du rapport */}
          <div className="osint-card p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-white mb-4">Aperçu du rapport</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Modèle sélectionné</span>
                <span className="text-white font-medium">
                  {reportTemplatesUI.find((t: ReportTemplateUI) => t.id === selectedTemplate)?.name}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Date de création</span>
                <span className="text-white font-medium">
                  {currentEnquete?.dateCreation || 'N/A'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Format</span>
                <span className="text-white font-medium">PDF</span>
              </div>
            </div>

            <div className="mt-6 p-4 bg-osint-light-gray rounded-lg">
              <h4 className="text-white font-medium mb-2">Sections incluses :</h4>
              <div className="grid grid-cols-2 gap-2 text-sm text-gray-300">
                {(() => {
                  const template = reportTemplatesUI.find((t: ReportTemplateUI) => t.id === selectedTemplate);
                  if (!template || !template.sections) return null;
                  
                  return template.sections.map((section, index) => {
                    if (!section || !section.visible) return null;
                    
                    return (
                      <div key={index} className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-osint-orange rounded-full"></div>
                        <span>{section.title}</span>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        </div>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="osint-card p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-white mb-4">Historique des rapports</h3>
          <p className="text-gray-400">Fonctionnalité en développement...</p>
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="osint-card p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-white mb-4">Analytics</h3>
          <p className="text-gray-400">Fonctionnalité en développement...</p>
        </div>
      )}

    </div>
  );
};

export default ReportsManager; 